// Archive countdown helpers. An event auto-archives 48h after its last session ends.
// Dates in the DB are stored as ISO YYYY-MM-DD (Australian civic calendar) and session
// times as HH:MM, so we mirror the backend logic (_event_end_utc, UTC+10 nominal offset).

export const AUTO_ARCHIVE_HOURS = 48;

/**
 * @param {object} ev — event object (must have `days: [{date, sessions: [{end}]}]`)
 * @returns {Date|null} — UTC Date of when the last session ends, or null if unresolvable.
 */
export function eventEndUtc(ev) {
  if (!ev || !Array.isArray(ev.days) || ev.days.length === 0) return null;
  const dates = ev.days.map((d) => d.date).filter(Boolean).sort();
  const latestDate = dates[dates.length - 1];
  if (!latestDate) return null;
  const latestDay = ev.days.find((d) => d.date === latestDate);
  let endTime = "23:59";
  if (latestDay && Array.isArray(latestDay.sessions)) {
    const ends = latestDay.sessions
      .map((s) => (s?.end || "").trim())
      .filter((t) => t.length === 5 && t[2] === ":");
    if (ends.length) endTime = ends.sort()[ends.length - 1];
  }
  const [y, m, d] = latestDate.split("-").map(Number);
  const [hh, mm] = endTime.split(":").map(Number);
  if (!y || !m || !d) return null;
  // Interpret the naive local time as UTC+10 (Australian nominal). Convert to UTC by
  // subtracting 10h. Matches backend _event_end_utc.
  const localMs = Date.UTC(y, m - 1, d, hh, mm);
  return new Date(localMs - 10 * 3600 * 1000);
}

/**
 * @returns {{state: 'upcoming'|'in_progress'|'grace'|'overdue'|'archived', endsAt: Date|null, archivesAt: Date|null, remainingMs: number}}
 * - upcoming: event hasn't started (based on first day's earliest session start — best-effort by using end - some heuristic)
 * - in_progress: now is before endsAt
 * - grace: now is between endsAt and archivesAt (still published; countdown active)
 * - overdue: past archivesAt but event is still published (scheduler hasn't run yet)
 * - archived: ev.status === 'archived'
 */
export function computeArchiveState(ev) {
  if (!ev) return { state: "upcoming", endsAt: null, archivesAt: null, remainingMs: 0 };
  if (ev.status === "archived") {
    return { state: "archived", endsAt: eventEndUtc(ev), archivesAt: null, remainingMs: 0 };
  }
  const endsAt = eventEndUtc(ev);
  if (!endsAt) return { state: "in_progress", endsAt: null, archivesAt: null, remainingMs: 0 };
  const archivesAt = new Date(endsAt.getTime() + AUTO_ARCHIVE_HOURS * 3600 * 1000);
  const now = new Date();
  if (now < endsAt) return { state: "in_progress", endsAt, archivesAt, remainingMs: endsAt - now };
  if (now < archivesAt) return { state: "grace", endsAt, archivesAt, remainingMs: archivesAt - now };
  return { state: "overdue", endsAt, archivesAt, remainingMs: 0 };
}

/**
 * @param {number} ms
 * @returns {string} — "12h 34m" or "45m" or "2d 3h" — human-friendly compact form.
 */
export function formatDuration(ms) {
  if (ms <= 0) return "0m";
  const totalMin = Math.floor(ms / 60000);
  const d = Math.floor(totalMin / (60 * 24));
  const h = Math.floor((totalMin % (60 * 24)) / 60);
  const m = totalMin % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}
